import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '.';
import { CardOption, SimilarCardSlim, TaskKind, TrainingCard, TrainingCardData, TrainingType } from '../types/TrainingTypes';
import { trainingActionCreators } from './Training';

interface MainState {
    task: string,
    taskAux: string | undefined,
    taskForms: string | undefined,

    solution: string | undefined,
    maskedSolution: string | undefined,

    solutionAux: string | undefined,
    maskedSolutionAux: string | undefined,

    solutionForms: string | undefined,
    maskedSolutionForms: string | undefined,

    example: string | undefined,
    isExampleShown: boolean,
    similar: SimilarCardSlim[] | undefined,
    symbolsShownCount: number,

    isOptionsShown: boolean,
    optionCards: TrainingCard[],
    options: CardOption[],
    chosenOption: CardOption | undefined,
    wrongOption: CardOption | undefined,
    backgrounds: string[],
    shownTime: string | undefined,
}

const initialState: MainState = {
    task: '',
    taskAux: '',
    taskForms: '',

    solution: '',
    maskedSolution: '',
    solutionAux: '',
    solutionForms: '',
    maskedSolutionAux: '',
    maskedSolutionForms: '',

    example: '',
    isExampleShown: false,

    similar: undefined,
    symbolsShownCount: 0,

    isOptionsShown: false,
    optionCards: [],
    options: [],
    chosenOption: undefined,
    wrongOption: undefined,
    backgrounds: ['', '', '', ''],
    shownTime: undefined,
}

const slice = createSlice({
    name: 'main',
    initialState,
    reducers: {
        setNextCard: (state, action: PayloadAction<TrainingCard>) => {
            const card = action.payload;
            state.optionCards = [card, ...card.options!];
            state.symbolsShownCount = 0;
            state.isExampleShown = card.resultInfo.isWeak && card.resultInfo.reviews >= 50 && card.modeInfo.isDirect;
            state.isOptionsShown = false;
            updateCardParts(state, card, false, false);
            updateUserChoice(state, card, false)
        },

        showHint: (state, action: PayloadAction<{ card: TrainingCard, symbolsShownCount: number }>) => {
            const { card, symbolsShownCount } = action.payload;
            state.symbolsShownCount = symbolsShownCount;
            updateCardParts(state, card!, false, false);
            updateUserChoice(state, card!, false);
        },

        toggleExample: state => {
            if (!state.isExampleShown)
                state.isExampleShown = true;
            else if (state.taskForms)
                state.isExampleShown = false;
        },

        showOptions: (state, action: PayloadAction<{ card: TrainingCard, shownTime: string }>) => {
            const { card, shownTime } = action.payload;
            state.isOptionsShown = true;

            state.optionCards = [card, ...card.options!];
            state.shownTime = shownTime;

            updateCardParts(state, card, true, true)
        },

        chooseOption: (state, action: PayloadAction<{ option: CardOption | undefined, card: TrainingCard }>) => {
            state.chosenOption = action.payload.option;
            updateUserChoice(state, action.payload.card, true)
        },

        resetTimer: (state, action: PayloadAction<string>) => {
            state.shownTime = action.payload;
        },


        showSolution: (state, action: PayloadAction<TrainingCard>) => {
            const card = action.payload;
            state.isExampleShown = true;
            updateCardParts(state, card, true, state.isOptionsShown);
            updateUserChoice(state, card, true);
        },
    },

    extraReducers: (builder) => {
        builder.addCase('training/reset', (state, action) => {
            return initialState;
        });

        builder.addCase('training/refreshCardData', (state, action) => {
            const data = (action as PayloadAction<TrainingCardData>).payload;
            if (data.card?.trainingType === TrainingType.Main) {
                updateCardParts(state, data.card!, data.isSolutionShown, false);
                updateUserChoice(state, data.card!, data.isSolutionShown);
            }
        });
    }
});

function updateCardParts(state: MainState, card: TrainingCard, isSolutionShown: boolean, isOptionsShown: boolean) {
    const ts = getCardParts(card, isSolutionShown, state.optionCards);
    state.task = ts.task;
    state.taskAux = ts.taskAux;
    state.taskForms = ts.taskForms;
    state.solution = ts.solution.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
    state.solutionAux = ts.solutionAux?.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
    state.solutionForms = ts.solutionForms?.replace(/;;/gi, '\r\n').replace(/\r\n;/gi, ';\r\n');
    state.options = ts.options;
}

function getCardParts(card: TrainingCard, isSolutionShown: boolean, optionCards: TrainingCard[]) {
    const k = card.modeInfo.modeSetting.taskKind;
    const isCardDirect = card.modeInfo.isDirect;

    const isPhraseFirst = !card.pronunciation || k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation || k === TaskKind.PhraseForms || k === TaskKind.Phrase;
    const isPronunciationFirst = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase || k === TaskKind.PronunciationForms || k === TaskKind.Pronunciation)

    const isPhraseSecond = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase);
    const isPronunciationSecond = card.pronunciation && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation);

    const isFormsThird = card.forms && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhraseForms || k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationForms)

    const first = isPronunciationFirst ? card.pronunciation : card.phrase;
    const second = isPhraseSecond ? card.phrase : isPronunciationSecond ? card.pronunciation : '';
    const third = isFormsThird ? card.forms : '';

    const options = optionCards
        .map(vc => {
            const first = isPronunciationFirst ? vc.pronunciation : vc.phrase;
            const second = isPhraseSecond ? vc.phrase : isPronunciationSecond ? vc.pronunciation : '';
            const third = isFormsThird ? vc.forms : '';

            const content = isCardDirect ? vc.meaning : first;
            const contentAux = isCardDirect ? '' : second;
            const contentForms = isCardDirect ? '' : third;

            const wrongOption = isCardDirect ? vc.phrase : vc.meaning;
            const width = getTextWidth(content, 20) + getTextWidth(contentAux, 15);
            return {
                content,
                contentAux,
                contentForms,
                wrongOption,
                card: vc,
                width
            };
        })
        .sort((a, b) => a.width - b.width);

    return {
        task: isCardDirect ? first : card.meaning,
        taskAux: isCardDirect ? second : '',
        taskForms: isCardDirect ? third : '',
        solution: isCardDirect ? card.meaning : first,
        solutionAux: isCardDirect ? '' : second,
        solutionForms: isCardDirect ? '' : third,
        options: options,
    }
}

function updateUserChoice(state: MainState, card: TrainingCard, isSolutionShown: boolean) {
    const { solution, solutionAux, solutionForms } = state;
    if (isSolutionShown)
        state.symbolsShownCount = solution!.length;

    state.maskedSolution = state.symbolsShownCount > 0 ? solution!.substring(0, state.symbolsShownCount) + solution!.substring(state.symbolsShownCount).replace(/./gi, '_') : '';

    state.maskedSolutionAux = isSolutionShown ? solutionAux : '';
    state.maskedSolutionForms = isSolutionShown ? solutionForms : '';
    state.example = card?.example?.replace(/([;?!.]);/gi, '$1\r\n');
    state.similar = isSolutionShown ? card!.similar : undefined;

    if (isSolutionShown) {
        const backgrounds = ['', '', '', ''];

        const chosenIndex = state.chosenOption ? state.options.findIndex(x => x.card.id === state.chosenOption?.card.id) : -1;
        const isCorrect = state.solution === state.chosenOption?.content;

        if (isCorrect)
            backgrounds[chosenIndex] = 'ch-green';
        else {
            const correctIndex = state.options.findIndex(x => x.card.id === card.id);
            backgrounds[correctIndex] = 'ch-green';
            backgrounds[chosenIndex === -1 ? backgrounds.length : chosenIndex] = 'ch-red';
        }

        state.backgrounds = backgrounds;
        state.wrongOption = !isCorrect && chosenIndex !== -1 ? state.options[chosenIndex] : undefined;
    }
    else {
        state.backgrounds = ['', '', '', ''];
        state.wrongOption = undefined;
    }
}

function getTextWidth(text: string, size: number) {
    const canvas = (getTextWidth as any).canvas || ((getTextWidth as any).canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = `bold ${size}pt arial`;
    const metrics = context.measureText(text);
    return metrics.width as number;
}

const setNextCard = (card: TrainingCard) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.setNextCard(card));
    }
}

const showHint = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState().main;
        const training = getState().training;

        const ts = getCardParts(training.card!, false, state.optionCards);
        const symbolsShownCount = getHintSymbolsShownCount(state.symbolsShownCount, ts.solution);
        if (symbolsShownCount === ts.solution.length)
            dispatch(showSolution());
        else {
            dispatch(slice.actions.showHint({
                card: training.card!,
                symbolsShownCount,
            }));
        }
    }
};

function getHintSymbolsShownCount(symbolsShownCount: number, solution: string) {
    symbolsShownCount++;

    if (symbolsShownCount < solution.length) {
        const match = /^[ .,?!:;[]()-]+/.exec(solution.substring(symbolsShownCount));
        if (match)
            symbolsShownCount += match[0].length;
    }
    else
        symbolsShownCount = solution.length;
    return symbolsShownCount;
}

const showSolution = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;

        dispatch(trainingActionCreators.showSolution(false));
        dispatch(slice.actions.showSolution(training.card!));
    }
};
const showOptions = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;

        dispatch(slice.actions.showOptions({ card: training.card!, shownTime: new Date().toISOString() }));
    }
};

const chooseOption = (option: CardOption | undefined) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;
        const state = getState().main;

        const isMistake = option?.content !== state.solution;
        dispatch(trainingActionCreators.showSolution(isMistake));
        dispatch(slice.actions.chooseOption({ option, card: training.card! }));
    }
};

const resetTimer = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.resetTimer(new Date().toISOString()));
    }
}

export const mainReducer = slice.reducer;

export const mainActionCreators = {
    setNextCard,
    showHint,
    toggleExample: slice.actions.toggleExample,
    showOptions,
    showSolution,
    resetTimer,
    chooseOption,
}
